import { AspectRatioIcon } from '../../../common/AspectRatioIcon';
import { useMemo } from 'react';
import styles from './ImageEditSidebar.module.css';
import '../../../../styles/form-controls.css';
import type { AspectRatio } from './ImageEditCanvas';
import type { EditState } from '../types';
import { ListTooltip } from '../../../common/ListTooltip';
import { getAspectRatioLabel } from '../../../../utils/aspectRatio';

type ResolutionOption = {
  id: string;
  label: string;
  width: number;
  height: number;
};

type Props = {
  width: number | null;
  height: number | null;
  selectedResolutionId: string | null;
  cropAspectRatio: AspectRatio;
  padding: 'none' | 'white' | 'transparent';
  format: string;
  quality: 'low' | 'medium-low' | 'medium' | 'medium-high' | 'high';
  onChange: (next: Partial<EditState>) => void;
  onCropAspectRatioChange: (ratio: AspectRatio) => void;
  platformOptions: Array<{ id: string; name: string }>;
  menuOptions: Array<{ id: string; label: string; platformId: string; description?: string | null }>;
  specOptions: Array<{
    id: string;
    label: string;
    description?: string | null;
    origin?: 'global' | 'custom';
    guidelines: string[];
    menuGuidelines: string[];
    sizeSummary?: string;
    allowedAspectRatios?: AspectRatio[];
  }>;
  selectedPlatformId: string | null;
  selectedMenuId: string | null;
  selectedSpecId: string | null;
  onSelectPlatform: (platformId: string | null) => void;
  onSelectMenu: (menuId: string | null) => void;
  onSelectSpec: (specId: string | null) => void;
  platformLoading?: boolean;
  menuLoading?: boolean;
  specLoading?: boolean;
  menuGuidelines: string[];
  guidelines: string[];
  resolutionPresets?: Record<AspectRatio, ResolutionOption[]>;
  allowedAspectRatios?: AspectRatio[] | null;
};

const QUALITY_OPTIONS: Array<{ value: Props['quality']; label: string }> = [
  { value: 'low', label: '圧縮率高 (ファイル小)' },
  { value: 'medium-low', label: '中 (軽量)' },
  { value: 'medium', label: '標準' },
  { value: 'medium-high', label: '中 (高画質)' },
  { value: 'high', label: '圧縮率低 (高画質)' },
];

const CROP_ASPECT_RATIO_OPTIONS: Array<{ value: AspectRatio; label: string }> = [
  { value: 'custom', label: 'カスタム' },
  ...(['1:1', '4:3', '3:4', '16:9', '9:16', '1.91:1', '1:1.91', '2:3', '3:2', '4:5'] as AspectRatio[]).map(
    (ratio) => ({ value: ratio, label: getAspectRatioLabel(ratio) })
  ),
];

// アスペクト比ごとの解像度プリセット
export const DEFAULT_RESOLUTION_PRESETS: Record<AspectRatio, ResolutionOption[]> = {
  'custom': [],
  '16:9': [
    { id: '16:9-1920x1080', label: '1920×1080 (Full HD)', width: 1920, height: 1080 },
    { id: '16:9-1600x900', label: '1600×900 (Amazonブランド広告)', width: 1600, height: 900 },
    { id: '16:9-1280x720', label: '1280×720 (HD)', width: 1280, height: 720 },
    { id: '16:9-1200x628', label: '1200×628 (Amazon商品画像)', width: 1200, height: 628 },
  ],
  '1:1': [
    { id: '1:1-1200x1200', label: '1200×1200', width: 1200, height: 1200 },
    { id: '1:1-1080x1080', label: '1080×1080 (楽天広告)', width: 1080, height: 1080 },
    { id: '1:1-800x800', label: '800×800', width: 800, height: 800 },
    { id: '1:1-300x300', label: '300×300', width: 300, height: 300 },
  ],
  '9:16': [
    { id: '9:16-1080x1920', label: '1080×1920 (縦型動画)', width: 1080, height: 1920 },
    { id: '9:16-720x1280', label: '720×1280', width: 720, height: 1280 },
    { id: '9:16-628x1200', label: '628×1200', width: 628, height: 1200 },
  ],
  '4:3': [
    { id: '4:3-1600x1200', label: '1600×1200', width: 1600, height: 1200 },
    { id: '4:3-1024x768', label: '1024×768', width: 1024, height: 768 },
    { id: '4:3-800x600', label: '800×600', width: 800, height: 600 },
  ],
  '3:4': [
    { id: '3:4-1200x1600', label: '1200×1600', width: 1200, height: 1600 },
    { id: '3:4-768x1024', label: '768×1024', width: 768, height: 1024 },
    { id: '3:4-600x800', label: '600×800', width: 600, height: 800 },
  ],
  '1.91:1': [
    { id: '1.91:1-1200x628', label: '1200×628', width: 1200, height: 628 },
  ],
  '1:1.91': [
    { id: '1:1.91-628x1200', label: '628×1200', width: 628, height: 1200 },
  ],
  '2:3': [],
  '3:2': [],
  '4:5': [],
};

export function ImageEditSidebar({
  width,
  height,
  selectedResolutionId,
  cropAspectRatio,
  // padding, // 機能未実装のため非表示
  format,
  quality,
  onChange,
  onCropAspectRatioChange,
  platformOptions,
  menuOptions,
  specOptions,
  selectedPlatformId,
  selectedMenuId,
  selectedSpecId,
  onSelectPlatform,
  onSelectMenu,
  onSelectSpec,
  platformLoading = false,
  menuLoading = false,
  specLoading = false,
  menuGuidelines,
  guidelines,
  resolutionPresets,
  allowedAspectRatios,
}: Props) {
  const resolutionMap = resolutionPresets ?? DEFAULT_RESOLUTION_PRESETS;
  const availableResolutions = resolutionMap[cropAspectRatio] || [];
  const isCustom = cropAspectRatio === 'custom';
  const hasRatioRestrictions = Boolean(allowedAspectRatios && allowedAspectRatios.length > 0);

  const aspectRatioOptions = useMemo(() => {
    if (hasRatioRestrictions && allowedAspectRatios) {
      const uniqueValues = Array.from(new Set(allowedAspectRatios));
      return uniqueValues
        .map((value) => {
          const predefined = CROP_ASPECT_RATIO_OPTIONS.find((option) => option.value === value);
          return predefined ?? { value, label: value };
        })
        .filter((option, index, self) => self.findIndex((item) => item.value === option.value) === index);
    }

    return CROP_ASPECT_RATIO_OPTIONS;
  }, [allowedAspectRatios, hasRatioRestrictions]);

  const renderGuidelineChip = (label: string, items: string[]) => {
    const filteredItems = items.filter(Boolean);
    if (!filteredItems.length) {
      return null;
    }
    return (
      <ListTooltip items={filteredItems} title={label}>
        <span className={styles.guidelineChip}>
          {label}
        </span>
      </ListTooltip>
    );
  };

  const handleChange = (payload: Partial<EditState>) => {
    onChange(payload);
  };

  return (
    <aside className={styles.sidebar}>
      {/* 媒体セクション */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>媒体</h3>

        <label className="gen-label">
          <span>プラットフォーム</span>
          <select
            value={selectedPlatformId ?? ''}
            onChange={(e) => onSelectPlatform(e.target.value || null)}
            disabled={platformLoading}
            className={selectedPlatformId ? 'has-value' : ''}
          >
            <option value="">未選択</option>
            {platformOptions.map((platform) => (
              <option key={platform.id} value={platform.id}>
                {platform.name}
              </option>
            ))}
          </select>
          {platformLoading && <span className={styles.helperText}>読み込み中…</span>}
        </label>

        {selectedPlatformId && (
          <div className={styles.nestedSection}>
            <label className="gen-label">
              <span>メニュー</span>
              <select
                value={selectedMenuId ?? ''}
                onChange={(e) => onSelectMenu(e.target.value || null)}
                disabled={menuLoading}
                className={selectedMenuId ? 'has-value' : ''}
              >
                <option value="">選択してください</option>
                {menuOptions.map((menu) => (
                  <option key={menu.id} value={menu.id}>
                    {menu.label}
                  </option>
                ))}
              </select>
              {menuLoading && <span className={styles.helperText}>読み込み中…</span>}
            </label>

            {selectedMenuId && (
              <label className="gen-label">
                <span>出力画像</span>
                <select
                  value={selectedSpecId ?? ''}
                  onChange={(e) => onSelectSpec(e.target.value || null)}
                  disabled={specLoading || specOptions.length === 0}
                  className={selectedSpecId ? 'has-value' : ''}
                >
                  <option value="">未選択</option>
                  {specOptions.map((spec) => (
                    <option key={spec.id} value={spec.id}>
                      {spec.label}
                      {spec.origin === 'custom' ? '（カスタム）' : ''}
                    </option>
                  ))}
                </select>
                {specLoading && <span className={styles.helperText}>読み込み中…</span>}
              </label>
            )}
          </div>
        )}

        <div className="gen-option-group">
          <span className="gen-option-group-label">アスペクト比</span>
          <div className={styles.aspectRatioButtons}>
            <button
              type="button"
              className={`${styles.aspectRatioNoneButton} ${cropAspectRatio === 'custom' ? styles.aspectRatioNoneButtonActive : ''}`}
              onClick={() => {
                onCropAspectRatioChange('custom');
                handleChange({ selectedResolutionId: null, width: null, height: null });
              }}
              disabled={hasRatioRestrictions && !aspectRatioOptions.some(o => o.value === 'custom')}
            >
              指定なし
            </button>
            {['16:9', '9:16', '1:1', '1.91:1'].map((ratio) => {
              const isDisabled = hasRatioRestrictions && !aspectRatioOptions.some(o => o.value === ratio);
              return (
                <button
                  key={ratio}
                  type="button"
                  className={`${styles.aspectRatioButton} ${cropAspectRatio === ratio ? styles.aspectRatioButtonActive : ''}`}
                  onClick={() => {
                    onCropAspectRatioChange(ratio as AspectRatio);
                    handleChange({ selectedResolutionId: null, width: null, height: null });
                  }}
                  title={ratio}
                  disabled={isDisabled}
                >
                  <AspectRatioIcon ratio={ratio} className={styles.aspectRatioIcon} />
                </button>
              );
            })}
            <select
              value={['16:9', '9:16', '1:1', '1.91:1', 'custom'].includes(cropAspectRatio) ? '' : cropAspectRatio}
              onChange={(e) => {
                if (e.target.value) {
                  onCropAspectRatioChange(e.target.value as AspectRatio);
                  handleChange({ selectedResolutionId: null, width: null, height: null });
                }
              }}
              className={`${styles.aspectRatioOtherSelect} ${cropAspectRatio && !['16:9', '9:16', '1:1', '1.91:1', 'custom'].includes(cropAspectRatio) ? 'has-value' : ''}`}
            >
              <option value="">その他</option>
              {aspectRatioOptions
                .filter((option) => !['16:9', '9:16', '1:1', '1.91:1', 'custom'].includes(option.value))
                .sort((a, b) => {
                  // 1:1.91 を最上位に（1.91:1はボタンに移動）
                  if (a.value === '1:1.91') return -1;
                  if (b.value === '1:1.91') return 1;
                  return 0;
                })
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* 出力解像度（アスペクト比が選択されている場合） */}
        {!isCustom && availableResolutions.length > 0 && (
          <label className="gen-label">
            <span>出力解像度</span>
            <select
              value={selectedResolutionId ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (!value) {
                  handleChange({ selectedResolutionId: null, width: null, height: null });
                  return;
                }
                const resolution = availableResolutions.find((item) => item.id === value);
                if (resolution) {
                  handleChange({
                    selectedResolutionId: resolution.id,
                    width: resolution.width,
                    height: resolution.height,
                  });
                }
              }}
              className={selectedResolutionId ? 'has-value' : ''}
            >
              <option value="">選択してください</option>
              {availableResolutions.map((resolution) => (
                <option key={resolution.id} value={resolution.id}>
                  {resolution.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* カスタム解像度（カスタムの場合のみ） */}
        {isCustom && (
          <div className={styles.customSizeInput}>
            <span className={styles.customSizeLabel}>カスタム解像度</span>
            <div className={styles.customSizeRow}>
              <input
                type="number"
                value={width ?? ''}
                onChange={(e) => handleChange({ width: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder="幅"
                min={1}
              />
              <span>×</span>
              <input
                type="number"
                value={height ?? ''}
                onChange={(e) => handleChange({ height: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder="高さ"
                min={1}
              />
              <span>px</span>
            </div>
          </div>
        )}

        {/* ガイドライン */}
        {(menuGuidelines.length > 0 || guidelines.length > 0) && (
          <div className={styles.guidelineChipGroup}>
            {renderGuidelineChip('メニュー規定', menuGuidelines)}
            {renderGuidelineChip('個別規定', guidelines)}
          </div>
        )}
      </section>

      {/* フォーマットセクション */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>フォーマット</h3>

        <label className="gen-label">
          <span>出力フォーマット</span>
          <select value={format} onChange={(e) => handleChange({ format: e.target.value })}>
            <option value="png">PNG</option>
            <option value="jpg">JPG</option>
            <option value="webp">WebP</option>
          </select>
        </label>

        {format !== 'png' && (
          <label className="gen-label">
            <span>圧縮率</span>
            <select value={quality} onChange={(e) => handleChange({ quality: e.target.value as Props['quality'] })}>
              {QUALITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </section>
    </aside>
  );
}
